import React, {
  CSSProperties,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import * as S from './styles'

type MarqueeProps = PropsWithChildren<{
  /**
   * The speed of the marquee in pixels per second
   *
   * @example
   * ```tsx
   * <Marquee speed={100}>Hello world</Marquee>
   * ```
   *
   * @default 140
   *
   */
  speed?: number

  /**
   * The animation-delay CSS property specifies the amount of time to wait from applying the animation to an element before beginning to perform the animation.
   *
   * @example
   * ```tsx
   * <Marquee delay="1s">Hello world</Marquee>
   * ```
   *
   * @default '1s'
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/animation-delay
   *
   */
  delay?: CSSProperties['animationDelay']

  /**
   * The animation-iteration-count CSS property sets the number of times an animation sequence should be played before stopping.
   *
   * @example
   * ```tsx
   * <Marquee iterationCount="infinite">Hello world</Marquee>
   * ```
   *
   * @default 'infinite'
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/animation-iteration-count
   *
   */
  iterationCount?: CSSProperties['animationIterationCount']

  /**
   * The animation-direction CSS property sets whether an animation should play forwards, backwards, or alternating back and forth.
   *
   * @example
   * ```tsx
   * <Marquee direction="normal">Hello world</Marquee>
   * ```
   *
   * @default 'normal'
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/animation-direction
   *
   */
  direction?: CSSProperties['animationDirection']

  /**
   * The function onAnimationIteration is called when the animation is repeated.
   *
   * @example
   * ```tsx
   * <Marquee onAnimationIteration={() => console.log('Hello world')}>Hello world</Marquee>
   * ```
   *
   */
  onAnimationIteration?: () => void
}>

export const Marquee: React.FC<MarqueeProps> = ({
  speed = 140,
  delay = '1s',
  iterationCount = 'infinite',
  direction = 'normal',
  onAnimationIteration,

  children,
}) => {
  const [containerWidth, setContainerWidth] = useState(0)
  const [marqueeWidth, setMarqueeWidth] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const marqueeRef = useRef<HTMLDivElement>(null)

  const duration = useMemo(
    () =>
      `${String(
        marqueeWidth < containerWidth
          ? containerWidth / speed
          : marqueeWidth / speed,
      )}s`,
    [marqueeWidth, containerWidth, speed],
  )

  useEffect(() => {
    const calculateWidth = () => {
      if (marqueeRef.current && containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width)
        setMarqueeWidth(marqueeRef.current.getBoundingClientRect().width)
      }
    }

    calculateWidth()

    window.addEventListener('resize', calculateWidth)

    return () => {
      window.removeEventListener('resize', calculateWidth)
    }
  }, [])

  return (
    <S.Container ref={containerRef}>
      <S.ScrollingText
        ref={marqueeRef}
        iterationCount={iterationCount}
        delay={delay}
        direction={direction}
        duration={duration}
        onAnimationIteration={onAnimationIteration}
      >
        {children}
      </S.ScrollingText>
    </S.Container>
  )
}
